import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import styled from 'styled-components';
import socket from "utils/Socket";
import storage from "utils/Storage";
import C from 'utils/Constant';
import { getUID, wait, sendNotfication, API } from 'utils/Helper';
import axios from 'axios';
import { formatUserDate } from 'utils/timezoneHelper';

const PageContainer = styled.div`
  min-height: 80vh;
  color: #fff;
  padding: 24px 16px;
  font-family: Arial, sans-serif;
  max-width: 1100px;
  margin: 0 auto;
  @media (max-width: 768px) {
    padding: 16px 12px;
  }
`;

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 24px;
  font-weight: 800;
  color: #fff;
  margin-bottom: 24px;
`;

const MainGrid = styled.div`
  display: grid;
  grid-template-columns: 220px 1fr;
  gap: 24px;
  min-width: 0;
  @media (max-width: 768px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 16px;
  }
`;

const Sidebar = styled.aside`
  background-color: #292d2e;
  padding: 8px;
  border-radius: 8px;
  height: fit-content;
  @media (max-width: 768px) {
    position: static;
    margin-bottom: 4px;
  }
`;

const SidebarNav = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  @media (max-width: 768px) {
    flex-direction: row;
    overflow-x: auto;
    white-space: nowrap;
    gap: 8px;
    padding-bottom: 4px;
    padding-left: 2px;
    padding-right: 2px;
    
    &::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
`;

const SidebarLink = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  border: none;
  padding: 12px 16px;
  color: ${props => props.active ? "#fff" : "#b9c4c7"};
  background-color: ${props => props.active ? "#3a4142" : "transparent"};
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  text-transform: capitalize;
  transition: all 0.2s ease;
  white-space: nowrap;
  cursor: pointer;
  &:hover {
    color: #fff;
    background-color: ${props => props.active ? "#3a4142" : "rgba(58, 65, 66, 0.4)"};
    text-decoration: none;
  }
  @media (max-width: 768px) {
    display: inline-block;
    padding: 8px 12px;
    font-size: 13px;
  }
`;

const Panel = styled.section`
  min-width: 0;
  width: 100%;
`;

const TableCard = styled.div`
  background-color: rgba(41, 45, 46, 0.4);
  border-radius: 8px;
  padding: 16px;
  overflow-x: auto;
  max-width: 100%;
  border: 1px solid rgba(58, 65, 66, 0.4);
  @media (max-width: 768px) {
    padding: 8px;
  }
`;

const TableContent = styled.div`
  min-width: 640px;
`;

const Row4 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.5fr 1fr 0.8fr;
  padding: 12px 8px;
  font-size: 14px;
  font-weight: ${props => props.header ? "bold" : "normal"};
  color: ${props => props.header ? "#b9c4c7" : "#fff"};
  border-bottom: 1px solid rgba(58, 65, 66, 0.4);
  align-items: center;
  transition: background-color 0.2s ease;
  
  ${props => !props.header && `
    &:hover {
      background-color: rgba(58, 65, 66, 0.2);
    }
  `}
`;

const ColLeft = styled.span`
  text-align: left;
`;

const ColCenter = styled.span`
  text-align: center;
`;

const ColRight = styled.span`
  text-align: right;
`;

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 0;
  color: #b9c4c7;
`;

const ClaimButton = styled.button`
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border: none;
  border-radius: 6px;
  color: #ffffff !important;
  cursor: pointer;
  font-size: 13px;
  font-weight: bold;
  padding: 6px 14px;
  transition: all 0.2s;
  text-transform: uppercase;

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #059669 0%, #047857 100%);
    transform: scale(1.05);
  }

  &:disabled {
    background: #3f3f46;
    color: #a1a1aa !important;
    cursor: not-allowed;
  }
`;

export default function Rewards() {
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [claimingId, setClaimingId] = useState(null);
  const [activeCategory, setActiveCategory] = useState("all");
  const [activeStatus, setActiveStatus] = useState("pending");
  
  const token = storage.getKey("token");
  const isLogged = !!token;

  useEffect(() => {
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;

    if (!isLogged) {
      setLoading(false);
      return;
    }

    const fetchRewards = async () => {
      try {
        const uid = getUID;
        if (!uid || isNaN(uid)) return;
        const res = await axios.get(`${API}/api/campaigns/claimable/${uid}?status=${activeStatus}`);
        if (res.data && res.data.success) {
          setRewards(res.data.rewards || []);
        }
      } catch (err) {
        console.error("Failed to load rewards:", err);
      } finally {
        setLoading(false);
      }
    };

    setLoading(true);
    const timer = setTimeout(() => fetchRewards(), 200);
    return () => clearTimeout(timer);
  }, [isLogged, activeStatus]);

  const handleClaim = async (claimId) => {
    setClaimingId(claimId);
    try {
      const uid = getUID;
      const res = await axios.post(`${API}/api/campaigns/claim`, {
        claimId: claimId,
        userId: uid
      });
      if (res.data && res.data.success) {
        sendNotfication(res.data.message || "Reward claimed!", "success", "top-center");
        setRewards(prev => prev.filter(r => r.claim_id !== claimId));
        socket.emit(C.CREDIT);
      } else {
        sendNotfication(res.data.message || "Failed to claim reward", "error", "top-center");
      }
    } catch (err) {
      console.error("Failed to claim:", err);
      sendNotfication("An error occurred while claiming.", "error", "top-center");
    } finally {
      setClaimingId(null);
    }
  };

  const categories = ["all", ...Array.from(new Set(rewards.map(r => r.category)))];
  const filteredRewards = activeCategory === "all" 
    ? rewards 
    : rewards.filter(r => r.category === activeCategory);

  return (
    <>
      <Helmet>
        <title>Rewards - Claim Bonuses</title>
      </Helmet>
      <PageContainer>
        <HeaderTitle>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#f6d365" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 12 20 22 4 22 4 12"></polyline><rect x="2" y="7" width="20" height="5"></rect><line x1="12" y1="22" x2="12" y2="7"></line><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"></path><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"></path></svg>
          Your Rewards
        </HeaderTitle>
        
        <MainGrid>
          <Sidebar>
            <SidebarNav>
              {categories.map(cat => (
                <SidebarLink 
                  key={cat} 
                  active={activeCategory === cat} 
                  onClick={() => setActiveCategory(cat)}
                >
                  {cat === "all" ? "All Rewards" : cat}
                </SidebarLink>
              ))}
            </SidebarNav>
          </Sidebar>

          <Panel>
            {/* Status Filter Dropdown */}
            <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '16px' }}>
              <select 
                value={activeStatus}
                onChange={(e) => setActiveStatus(e.target.value)}
                style={{
                  background: "#292d2e",
                  color: "#fff",
                  border: "1px solid rgba(58, 65, 66, 0.8)",
                  padding: "8px 16px",
                  borderRadius: "6px",
                  fontSize: "13px",
                  fontWeight: "600",
                  cursor: "pointer",
                  outline: "none"
                }}
              >
                <option value="pending">Active Rewards</option>
                <option value="expired">Expired Rewards</option>
                <option value="all">All Rewards</option>
              </select>
            </div>

            <TableCard>
              <TableContent>
                <Row4 header>
                  <ColLeft>Earned On</ColLeft>
                  <ColLeft>Reward Name</ColLeft>
                  <ColCenter>Category</ColCenter>
                  <ColRight>Action</ColRight>
                </Row4>

                {!isLogged ? (
                  <EmptyStateContainer>
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.4, marginBottom: 15 }}><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
                    Please login to see your rewards.
                  </EmptyStateContainer>
                ) : loading ? (
                  <EmptyStateContainer>Loading your rewards...</EmptyStateContainer>
                ) : filteredRewards.length === 0 ? (
                  <EmptyStateContainer>
                    <svg width="56" height="56" viewBox="0 0 56 56" fill="none" style={{ opacity: 0.4, marginBottom: 15 }}>
                      <rect x="8" y="32" width="10" height="16" rx="2" fill="currentColor" />
                      <rect x="23" y="22" width="10" height="26" rx="2" fill="currentColor" />
                      <rect x="38" y="14" width="10" height="34" rx="2" fill="currentColor" />
                      <circle cx="43" cy="14" r="3" fill="#1fc16b" />
                    </svg>
                    No pending rewards to claim.
                  </EmptyStateContainer>
                ) : (
                  filteredRewards.map((reward) => (
                    <Row4 key={reward.claim_id}>
                      <ColLeft>{formatUserDate(reward.triggered_at)}</ColLeft>
                      <ColLeft style={{ fontWeight: 'bold' }}>{reward.name}</ColLeft>
                      <ColCenter style={{ textTransform: 'uppercase', fontSize: '12px', color: '#94a3b8' }}>
                        {reward.category}
                      </ColCenter>
                      <ColRight>
                        {reward.action_status === "expired" ? (
                          <span style={{ color: "#ef4444", fontSize: "13px", fontWeight: "bold", textTransform: "uppercase", marginRight: "12px" }}>
                            Expired
                          </span>
                        ) : (
                          <ClaimButton 
                            onClick={() => handleClaim(reward.claim_id)}
                            disabled={claimingId === reward.claim_id}
                          >
                            {claimingId === reward.claim_id ? "Claiming..." : "Claim"}
                          </ClaimButton>
                        )}
                      </ColRight>
                    </Row4>
                  ))
                )}
              </TableContent>
            </TableCard>
          </Panel>
        </MainGrid>
      </PageContainer>
    </>
  );
}
